import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { authApi, type ChangePasswordInput } from '@/features/auth/auth.api';
import { useNotification } from '@/contexts/NotificationContext';
import { getErrorMessage } from '@/utils/errorHandling';

// Espelha a `passwordSchema` do backend (`kaive-service/src/utils/validators.ts`)
// para que a UX rejeite localmente antes do round-trip. Manter sincronizado.
const newPasswordSchema = z
  .string()
  .min(12, 'A senha deve ter no mínimo 12 caracteres')
  .max(128, 'A senha deve ter no máximo 128 caracteres')
  .regex(/[A-Z]/, 'A senha deve conter ao menos uma letra maiúscula')
  .regex(/[a-z]/, 'A senha deve conter ao menos uma letra minúscula')
  .regex(/[0-9]/, 'A senha deve conter ao menos um número')
  .regex(/[^A-Za-z0-9]/, 'A senha deve conter ao menos um caractere especial');

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Informe a senha atual'),
    newPassword: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Confirme a nova senha'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'As senhas não coincidem',
  });

type FormData = z.infer<typeof schema>;

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ApiErrorPayload {
  code?: string;
  error?: string;
}

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  const { showNotification } = useNotification();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (input: ChangePasswordInput) => authApi.changePassword(input),
    onSuccess: () => {
      showNotification('Senha alterada com sucesso', 'success');
      reset();
      onClose();
    },
    onError: err => {
      if (err instanceof AxiosError && err.response?.status === 400) {
        const payload = err.response.data as ApiErrorPayload | undefined;
        if (payload?.code === 'INVALID_CURRENT_PASSWORD') {
          setError('currentPassword', { message: 'Senha atual incorreta' });
          return;
        }
        if (payload?.code === 'SAME_AS_CURRENT_PASSWORD') {
          setError('newPassword', {
            message: 'A nova senha deve ser diferente da atual',
          });
          return;
        }
      }
      showNotification(getErrorMessage(err, 'Erro ao alterar senha'), 'error');
    },
  });

  const handleClose = () => {
    if (!changePasswordMutation.isPending) {
      reset();
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      onClose();
    }
  };

  const onSubmit = (data: FormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  const renderVisibilityToggle = (visible: boolean, toggle: () => void) => (
    <InputAdornment position="end">
      <Tooltip title={visible ? 'Ocultar senha' : 'Mostrar senha'}>
        <IconButton
          onClick={toggle}
          edge="end"
          size="small"
          disabled={changePasswordMutation.isPending}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Trocar senha
        </Typography>
        <Tooltip title="Fechar">
          <span>
            <IconButton
              onClick={handleClose}
              size="small"
              disabled={changePasswordMutation.isPending}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              A nova senha deve ter ao menos 12 caracteres, incluindo maiúscula, minúscula, número
              e caractere especial.
            </Typography>

            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Senha atual"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  disabled={changePasswordMutation.isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: renderVisibilityToggle(showCurrent, () =>
                        setShowCurrent(v => !v)
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nova senha"
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  disabled={changePasswordMutation.isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: renderVisibilityToggle(showNew, () => setShowNew(v => !v)),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirmar nova senha"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  disabled={changePasswordMutation.isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: renderVisibilityToggle(showConfirm, () =>
                        setShowConfirm(v => !v)
                      ),
                    },
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={changePasswordMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={changePasswordMutation.isPending}
            startIcon={
              changePasswordMutation.isPending ? <CircularProgress size={18} /> : null
            }
          >
            {changePasswordMutation.isPending ? 'Alterando...' : 'Alterar senha'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
